import type {
  DocPackCatalog,
  DocPackCharRange,
  DocPackOutlineItem,
} from '@listening/doc-pack';
import type { DocPackReadingAnchor } from './reading-plan';

export type DocPackOutlineNavigationItem = {
  id: string;
  title: string;
  page: number;
  charStart: number;
  depth: number;
};

const outlineCharStart = (chars: DocPackCharRange | undefined) =>
  chars?.[0] ?? 0;

function flattenOutlineItems(
  items: readonly DocPackOutlineItem[],
  depth: number,
  parentId: string,
): DocPackOutlineNavigationItem[] {
  return items.flatMap((item, index) => {
    const id = `${parentId}${index.toString()}`;
    const current = {
      id,
      title: item.title.trim() || `Page ${item.page.toString()}`,
      page: item.page,
      charStart: outlineCharStart(item.chars),
      depth,
    };
    return [
      current,
      ...flattenOutlineItems(item.children ?? [], depth + 1, `${id}.`),
    ];
  });
}

export function docPackOutlineNavigationItems(
  catalog: Pick<DocPackCatalog, 'outline' | 'pages'>,
): readonly DocPackOutlineNavigationItem[] {
  const outlineItems = flattenOutlineItems(catalog.outline, 0, '');
  if (outlineItems.length > 0) return outlineItems;
  return catalog.pages.map((page) => ({
    id: `page-${page.n.toString()}`,
    title: page.label ?? `Page ${page.n.toString()}`,
    page: page.n,
    charStart: 0,
    depth: 0,
  }));
}

export function activeDocPackOutlineNavigationItem(
  items: readonly DocPackOutlineNavigationItem[],
  anchor: DocPackReadingAnchor | undefined,
) {
  if (!anchor) return undefined;
  const idx = items.findLastIndex(
    (item) =>
      item.page < anchor.page ||
      (item.page === anchor.page && item.charStart <= anchor.charStart),
  );
  return idx === -1 ? undefined : items[idx];
}
